import React, { FC, useState } from 'react';
import { Moment } from 'moment';
import { Badge, Spin, message } from 'antd';
import Calendar from 'components/UI/Calendar';
import CloseDayConfirmationModal from 'components/views/OpenDays/CloseDayConfirmationModal';
import * as texts from 'assets/texts/canopyConfiguration';
import {
  API_DATE_FORMAT,
  AvailabilityChange,
  DaysAvailability,
  DISPLAY_DATE_FORMAT,
} from '../types';
import styles from './AvailabilityCalendar.module.scss';

interface AvailabilityCalendarProps {
  days: DaysAvailability;
  month: Moment;
  loading: boolean;
  onMonthChange: (month: Moment) => void;
  onApply: (change: AvailabilityChange) => Promise<boolean>;
}

const AvailabilityCalendar: FC<AvailabilityCalendarProps> = ({
  days,
  month,
  loading,
  onMonthChange,
  onApply,
}) => {
  const [selectedDay, setSelectedDay] = useState<Moment>();
  const [isConfirmationVisible, setIsConfirmationVisible] = useState<boolean>(false);

  const selectedDayKey = selectedDay?.format(API_DATE_FORMAT);
  const nextIsOpen = selectedDayKey ? !days[selectedDayKey] : false;

  const onSelect = (date: Moment) => {
    if (!date.isSame(month, 'month')) {
      onMonthChange(date);
      return;
    }
    const dayKey = date.format(API_DATE_FORMAT);
    if (days[dayKey] === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      message.info(texts.parkClosedDay);
      return;
    }
    setSelectedDay(date);
    setIsConfirmationVisible(true);
  };

  const confirmChange = async () => {
    if (selectedDayKey) {
      await onApply({
        startDate: selectedDayKey,
        endDate: selectedDayKey,
        isOpen: nextIsOpen,
      });
    }
  };

  const dateCellRender = (date: Moment) => {
    if (!date.isSame(month, 'month')) {
      return null;
    }
    const isOpen = days[date.format(API_DATE_FORMAT)];
    if (isOpen === undefined) {
      return <Badge status="default" text={texts.statusParkClosed} />;
    }
    return isOpen ? (
      <Badge status="success" text={texts.statusOpen} />
    ) : (
      <Badge status="error" text={texts.statusClosed} />
    );
  };

  return (
    <>
      <CloseDayConfirmationModal
        isModalVisible={isConfirmationVisible}
        setIsModalVisible={setIsConfirmationVisible}
        confirmationFunction={confirmChange}
        text={
          selectedDay
            ? texts.confirmDay(nextIsOpen, selectedDay.format(DISPLAY_DATE_FORMAT))
            : undefined
        }
      />
      <Spin spinning={loading}>
        <Calendar
          className={styles.Calendar}
          value={month}
          onSelect={onSelect}
          onPanelChange={onMonthChange}
          dateCellRender={dateCellRender}
        />
      </Spin>
    </>
  );
};

export default AvailabilityCalendar;
